import {useEffect, useRef} from 'react'
import {useSearchParams} from 'react-router-dom'
import {useShallow} from 'zustand/react/shallow'
import {useDebounce} from '@uidotdev/usehooks'
import {
    FILTER_URL_KEYS,
    isDefaultFilterValue,
    useListDealsStore,
} from '../../stores/listDealsStore'

// ─────────────────────────────────────────────────────────────────────────
// Two-way sync between the list-deals filter state in the store and the
// URL search params. Call this once at the top of ListDealsPage.
//
//   Mount → read every known filter key from the URL → hydrate the store.
//   Store change → debounce 300 ms → write non-default values back to the URL
//                  (replace, so we don't pollute history with every keystroke).
//
// History tradeoff: using `replace: true` means typing into filters does
// NOT create back-button steps. Refresh / share still works because the
// current URL is always up to date.
// ─────────────────────────────────────────────────────────────────────────
export const useFilterUrlSync = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const hydrateFilters = useListDealsStore((state) => state.hydrateFilters)

    // Subscribe to just the filter slice of the store.
    const filters = useListDealsStore(
        useShallow((state) => {
            const out = {}
            for (const k of Object.keys(FILTER_URL_KEYS)) out[k] = state[k]
            return out
        })
    )

    // Debounce so range-input typing doesn't repaint the URL on every key.
    const debouncedFilters = useDebounce(filters, 300)

    // Guard against the post-hydrate effect firing before hydrate runs and
    // wiping out URL params from a bookmark/share link.
    const hasHydrated = useRef(false)

    // ── Mount: hydrate store from URL (runs once). ──
    useEffect(() => {
        const update = {}
        for (const [storeKey, urlKey] of Object.entries(FILTER_URL_KEYS)) {
            const v = searchParams.get(urlKey)
            if (v !== null) update[storeKey] = v
        }
        hydrateFilters(update)
        hasHydrated.current = true
        // Intentionally empty deps — mount only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // ── After hydration: write debounced filters back to URL. ──
    useEffect(() => {
        if (!hasHydrated.current) return
        // Update only the managed filter keys; preserve any other params already
        // on the URL (e.g. pricing_id from the "view desked quotes" link). The
        // functional updater reads the latest params so we never clobber them with
        // a stale snapshot. Without this, rebuilding the URL from scratch dropped
        // every unmanaged param right after mount.
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            for (const [storeKey, urlKey] of Object.entries(FILTER_URL_KEYS)) {
                const v = debouncedFilters[storeKey]
                if (isDefaultFilterValue(storeKey, v)) next.delete(urlKey)
                else next.set(urlKey, v)
            }
            return next
        }, {replace: true})
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedFilters])
}
